import { useState } from 'react';
import beTwMerge from '@/utils/beTwMerge';
import RichText from '../RichText/RichText';
import CheckBox from './CheckBox';

export type InputType = {
  id: string;
  name: string;
  type: string;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  errorMessageClassName?: string;
  required?: boolean;
  label?: string;
  requiredLabel?: string;
  labelType?: 'upper' | 'inline';
  fullWidth?: boolean;
  errorColor?: string;
  customCheckboxValue?: boolean | undefined | null;
  registerObject?: any;
};

export default function Input({
  register,
  registerObject,
  name,
  fullWidth,
  label = '',
  requiredLabel = '',
  className = '',
  inputClassName = '',
  labelClassName = '',
  errorMessageClassName = '',
  error,
  labelType = 'upper',
  errorColor = '',
  customCheckboxValue = undefined,
  ...inputProps
}) {
  const [checked, setChecked] = useState(!!customCheckboxValue);
  const errorStyle = {
    input: errorColor
      ? '!tw-border-b-[#C21419] tw-placeholder-[#C21419] focus:tw-border-b-[#C21419] active:tw-border-b-[#C21419]'
      : '!tw-border-b-[#F90000] tw-placeholder-[#F90000] focus:tw-border-b-[#F90000] active:tw-border-b-[#F90000]',
    label: errorColor ? '!tw-text-[#C21419]' : '!tw-text-[#F90000]',
    message: errorColor ? 'tw-text-[#C21419]' : 'tw-text-[#F90000]',
  };
  if (registerObject?.required === true) {
    registerObject.required = `${requiredLabel || label} is required.`;
  }

  return (
    <div
      className={beTwMerge(
        'tw-relative tw-z-0 tw-w-full tw-font-book lg:[&_label]:!tw-tracking-[0]',
        inputProps.type !== 'checkbox'
          ? `tw-w-col-span-full ${fullWidth ? 'tw-col-span-full' : 'tw-col-span-1'}`
          : 'tw-col-span-full tw-flex tw-gap-x-2 tw-h-full tw-justify-start tw-flex-wrap',
        className
      )}
    >
      {inputProps.type === 'textarea' && (
        <div className={beTwMerge('tw-group tw-space-y-2', error && 'is-error')}>
          <label
            htmlFor={name}
            className="tw-mb-0 tw-text-sm tw-font-normal group-[.is-error]:tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75"
          >
            Message
          </label>
          <textarea
            {...register(name, { ...registerObject })}
            onKeyUp={(e: any) => {
              const keyCounter = e.target.value.length;
              document.getElementById('charCounter')!.innerHTML = `${(300 - keyCounter).toString()}`;
            }}
            name={name}
            aria-label={name}
            className="border-normal tw-z-20 tw-block tw-h-40 tw-w-full tw-rounded-none tw-border tw-border-gray tw-px-2 tw-pb-1.5 tw-pt-2.5 tw-text-sm tw-outline-none focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey focus-visible:tw-outline-0 group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
            maxLength={300}
          />
          <div className="tw-flex">
            {error && (
              <p
                className={beTwMerge(
                  'tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error',
                  errorMessageClassName
                )}
              >
                {error.message}
              </p>
            )}
            <span id="charCounter" className="tw-ml-auto tw-font-AvenirMedium tw-text-[10px] tw-text-grey500">
              300
            </span>
          </div>
        </div>
      )}

      {inputProps.type !== 'textarea' && inputProps.type !== 'checkbox' && (
        <div className={beTwMerge('tw-group tw-relative tw-z-0', error && 'is-error')}>
          {inputProps.type === 'number' && (
            <input
              className={beTwMerge(
                'border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border-b tw-border-darkGrey tw-bg-transparent tw-px-0 tw-pb-0 tw-pt-4.5 tw-text-sm tw-outline-none focus:tw-shadow-[0px_0.5px_0px_#2c2c2c] focus-visible:tw-outline-0 group-[.is-error]:tw-border-error group-[.is-error]:focus:tw-shadow-[0px_0.5px_0px_#c21419]',
                'tw-w-full',
                error && errorStyle.input,
                className,
                labelType,
                labelType === 'inline' && 'tw-pb-0'
              )}
              {...register(name, { ...registerObject })}
              {...inputProps}
              name={name}
              aria-label={name}
              placeholder=" "
              pattern="[0-9]*"
              inputMode="numeric"
            />
          )}

          {inputProps.type !== 'textarea' && inputProps.type !== 'checkbox' && inputProps.type !== 'number' && (
            <input
              className={beTwMerge(
                'border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border-b tw-border-darkGrey tw-bg-transparent tw-px-0 tw-pb-0 tw-pt-4.5 tw-text-sm tw-outline-none focus:tw-shadow-[0px_0.5px_0px_#2c2c2c] focus-visible:tw-outline-0 group-[.is-error]:tw-border-error group-[.is-error]:focus:tw-shadow-[0px_0.5px_0px_#c21419]',
                'tw-w-full',
                error && errorStyle.input,
                className,
                inputClassName,
                labelType,
                labelType === 'inline' && 'tw-pb-0'
              )}
              {...register(name, { ...registerObject })}
              {...inputProps}
              name={name}
              aria-label={name}
              placeholder=" "
            />
          )}
          {label && (
            <label
              className={beTwMerge(
                'tw-pointer-events-none tw-absolute tw-top-3.5 tw-mb-0 tw-origin-[0] -tw-translate-y-3 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-3 peer-focus:tw-scale-75 peer-focus:tw-text-grey500',
                error && errorStyle.label,
                labelType === 'inline' && 'tw-top-2',
                !!labelClassName && labelClassName
              )}
              htmlFor={name}
            >
              {!registerObject?.noRequiredSymbol && registerObject?.required && labelType !== 'inline'
                ? `${label}*`
                : label}
            </label>
          )}

          {error && (
            <p
              className={beTwMerge(
                'tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error',
                errorMessageClassName
              )}
            >
              {error.message}
            </p>
          )}
        </div>
      )}

      {inputProps.type === 'checkbox' && (
        <div>
          <div className="tw-flex tw-items-start">
            <input
              onClick={() => {
                setChecked(prevValue => !prevValue);
              }}
              className={beTwMerge(
                'border-normal !tw-mt-0 tw-size-[18px] tw-rounded-none tw-border tw-border-[#d9d9d9] tw-text-darkGreen tw-ring-0 checked:tw-border-current focus:!tw-outline-0 focus:tw-ring-0',
                error && 'tw-border-error',
                className,
                labelType
              )}
              {...register(name, { ...registerObject })}
              {...inputProps}
              id={name}
              name={name}
              data-tw="checkbox"
            />
            <div className="tw-ms-2">
              {label && (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label
                  className={beTwMerge(
                    'tw-relative tw-top-0.5 tw-mb-0 tw-text-xs tw-font-normal',
                    !!labelClassName && labelClassName
                  )}
                  htmlFor={name}
                  dangerouslySetInnerHTML={{ __html: label }}
                />
              )}
              {error && (
                <p className={beTwMerge('tw-mt-2 tw-mb-2 tw-text-[11px] tw-text-error', errorMessageClassName)}>
                  {error.message}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
